// Organisatie → Vestigingen (establishments register) — FRONTEND-BLUEPRINT §3.7; PLAN-W29. Re-uses the
// shared RegisterScreen/CreateDialog/EditDialog pattern (like the Inspecteur registers). Fields are the
// minimal grounded set (name + address); responsible_user is carried in the schema but its picker is a
// flagged follow-up. Gated vestiging.read / .write / .update / .delete.
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { TENANT_PATHS } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import type { Column } from "@/components/data-table";
import {
  RegisterScreen, CreateDialog, EditDialog, DeactivateButton, truncate, type Row, type FormField,
} from "../inspector/_shared";

export function VestigingenRoute() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["vestigingen"] });
  const [editing, setEditing] = useState<string | null>(null);

  const FIELDS: FormField[] = [
    { key: "name", label: t("vest.name"), required: true },
    { key: "address", label: t("vest.address") },
    { key: "postalCode", label: t("vest.postalCode") },
    { key: "city", label: t("vest.city") },
    { key: "country", label: t("vest.country") },
  ];

  const columns: Column<Row>[] = [
    { key: "name", header: t("vest.name"), render: (r) => <span className="font-medium">{String(r.name ?? "—")}</span> },
    { key: "address", header: t("vest.address"), render: (r) => truncate(r.address) },
    { key: "postalCode", header: t("vest.postalCode"), render: (r) => String(r.postalCode ?? "—") },
    { key: "city", header: t("vest.city"), render: (r) => String(r.city ?? "—") },
    { key: "country", header: t("vest.country"), render: (r) => String(r.country ?? "—") },
  ];

  return (
    <>
      <RegisterScreen
        title={t("vest.title")}
        description={t("vest.sub")}
        queryKey="vestigingen"
        listFn={() => api.listVestigingen()}
        columns={columns}
        empty={t("vest.empty")}
        readPerm="vestiging.read"
        actions={
          <Can perm="vestiging.write">
            <CreateDialog
              title={t("vest.new")}
              fields={FIELDS}
              onCreate={(i) => api.createVestiging(i as unknown as Parameters<typeof api.createVestiging>[0])}
              onDone={invalidate}
            />
          </Can>
        }
        rowActions={(r) => {
          const id = String(r.id);
          return (
            <span className="inline-flex items-center gap-1">
              <Can perm="vestiging.update"><button type="button" className="text-xs text-primary hover:underline" onClick={() => setEditing(id)}>{t("crud.edit")}</button></Can>
              <Can perm="vestiging.delete"><DeactivateButton basePath={TENANT_PATHS.vestigingen} id={id} onDone={invalidate} /></Can>
            </span>
          );
        }}
      />
      {editing && (
        <EditDialog
          basePath={TENANT_PATHS.vestigingen}
          id={editing}
          title={t("crud.edit")}
          fields={FIELDS}
          onClose={() => setEditing(null)}
          onDone={() => { invalidate(); setEditing(null); }}
        />
      )}
    </>
  );
}
